import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetParams } from './hooks/useGetParams';
import { useBroadcast } from './hooks/useBroadcast';
import { useNoiseLevel } from './hooks/useNoiseLevel';
import useLocalStorage from './hooks/useLocalStorage';

import { Title } from './components/Title/Title';
import { NoizeGraf } from './components/NoizeGraf/NoizeGraf';
import { NoizeValue } from './components/NoizeValue/NoizeValue';

import defaultBackground from './assets/images/bgPoster.png';

function App() {
  const { currentState, allStates } = useBroadcast();
  const [prevDelta, setPrevDelta] = useState(0);

  const [playerNumber, setPlayerNumber] = useLocalStorage('player', '88');

  const { bg, calibrationOffset, deltaDuration, smoothingFactor } =
    useGetParams();

  const { calibratedLevelDbSPL, currentMicrophone, addSmoothBoost } =
    useNoiseLevel({
      calibrationOffset,
      deltaDuration,
      smoothingFactor,
    });

  const lastExecutedRef = useRef(0);

  useEffect(() => {
    if (currentState !== 'S6') {
      return;
    }
    const now = Date.now();

    if (now - lastExecutedRef.current >= Number(deltaDuration)) {
      if (currentState === 'S6' && calibratedLevelDbSPL < 100) {
        let deltaFromName = 100 - calibratedLevelDbSPL;
        const delta = deltaFromName + prevDelta;
        setPrevDelta(delta);
        addSmoothBoost(deltaFromName);
      }
      lastExecutedRef.current = now;
    }
  }, [calibratedLevelDbSPL, currentState]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5'];

    if (!deltaStates.includes(currentState)) {
      return;
    }

    if (allStates && currentState) {
      let deltaFromName = 0;

      if (currentState === 'S1') {
        deltaFromName = 0;
      } else {
        deltaFromName = Number(allStates[currentState]?.value || 0) || 0;
      }

      if (deltaFromName !== prevDelta) {
        const delta = deltaFromName - prevDelta;

        setPrevDelta(deltaFromName);
        addSmoothBoost(delta);
      }
    }
  }, [currentState, allStates, addSmoothBoost, prevDelta]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];
    if (!currentState || deltaStates.includes(currentState)) {
      return;
    }

    const nameState = allStates[currentState]?.value;

    if (nameState) {
      const nameStateWords = nameState.split(' ');
      const number = nameStateWords[nameStateWords.length - 1];

      setPlayerNumber(number);
    }
  }, [currentState, allStates]);

  useEffect(() => {
    if (currentMicrophone?.label) {
      console.log(currentMicrophone?.label);
    }
  }, [currentMicrophone]);

  const srcVideoPlayer = useMemo(() => {
    const protocol = window?.location.protocol || 'https:';
    return `${protocol}//stream-test.iactive.pro/sibir-hk/videos/noize/${
      playerNumber || '88'
    }.webm`;
  }, [playerNumber]);

  const background = useMemo(() => {
    return bg && bg !== '0'
      ? `url(${bg === '1' ? defaultBackground : bg}) center/cover no-repeat`
      : `none`;
  }, [bg]);

  return (
    <div className="app-wrapper" style={{ background }}>
      <div className="titre-wrapper">
        <Title />

        <div className="noize-wrapper">
          <NoizeGraf noiseLevel={calibratedLevelDbSPL} />
          <NoizeValue noiseLevel={calibratedLevelDbSPL} />
        </div>
      </div>

      <motion.div
        key={srcVideoPlayer}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <video autoPlay="autoplay" muted="muted" loop id="bgVideo">
          <source src={srcVideoPlayer} type="video/webm"></source>
        </video>
      </motion.div>
    </div>
  );
}

export default App;
